import * as React from "react";
import { IoAnalyticsOutline } from "react-icons/io5";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { IAdvisorComparisonTableBlock } from "@types";

import { cn } from "../cn";
import {
  abbreviate,
  buildChart,
  ChartTooltip,
  isCountColumn,
  isMedianColumn,
  seriesColor,
  ViewToggle,
  // type DistributionView,
} from "./chartCommon";
import { ROW_STYLES } from "./sentiment";
import { numericColumns } from "./tableUtils";

/**
 * S1B_INTERNAL's peer distribution, rendered two ways: a table with a coloured marker per row
 * by default, or a line chart of the same numbers, switched by the toggle in the top-right. The
 * sample-count ("n") column is dropped from both views.
 */

function TableView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const numeric = numericColumns(block);
  const visible = block.columns
    .map((_, i) => i)
    .filter(i => !isCountColumn(block.columns[i]));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {visible.map(i => (
              <th
                key={i}
                className={cn(
                  "whitespace-nowrap px-3.5 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500",
                  i !== 0 && numeric[i] ? "text-right" : "text-left"
                )}
              >
                {block.columns[i]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr
              key={r}
              className={cn(
                "border-b border-gray-200 last:border-b-0",
                row.sentiment && ROW_STYLES[row.sentiment]
              )}
            >
              {visible.map(c => {
                const cell = row.cells[c] ?? "";
                if (c === 0) {
                  return (
                    <td key={c} className="whitespace-nowrap px-3.5 py-2.5">
                      <span className="flex items-center gap-2.5 font-medium text-gray-800">
                        <span
                          className={cn(
                            "h-2.5 w-2.5 shrink-0 rounded-sm",
                            seriesColor(r).dot
                          )}
                        />
                        {cell}
                      </span>
                    </td>
                  );
                }
                // The median is the headline of a distribution, so it carries the weight; the
                // rest of the spread sits back in muted text.
                const median = isMedianColumn(block.columns[c] ?? "");
                return (
                  <td
                    key={c}
                    className={cn(
                      "whitespace-nowrap px-3.5 py-2.5",
                      numeric[c] ? "text-right" : "text-left",
                      median ? "font-semibold text-gray-800" : "text-gray-500"
                    )}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LineChartView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const { series, data } = buildChart(block);

  return (
    <div className="px-1 pb-2 pt-3">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 20, bottom: 4, left: 4 }}
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="axis"
            tickLine={false}
            axisLine={false}
            dy={8}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <YAxis
            width={48}
            tickLine={false}
            axisLine={false}
            tickFormatter={abbreviate}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#E5E7EB" }} />
          {series.map(s => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.label}
              stroke={s.stroke}
              strokeWidth={2}
              dot={{ r: 3.5, fill: s.stroke, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DistributionTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  const [view, setView] = React.useState<DistributionView>("table");

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-end border-b border-gray-200 px-3 py-2">
        <ViewToggle
          view={view}
          onChange={setView}
          chartIcon={<IoAnalyticsOutline className="h-4 w-4" />}
        />
      </div>
      {view === "table" ? (
        <TableView block={block} />
      ) : (
        <LineChartView block={block} />
      )}
    </div>
  );
}
